import React, { useState } from 'react';
import { X } from 'lucide-react';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

const AddImportTemplate = ({ isOpen, onClose, onSubmit }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [templateName, setTemplateName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFields, setUploadedFields] = useState([]);
  const [fieldMappings, setFieldMappings] = useState({});
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [reviewMappings, setReviewMappings] = useState([]);

  const existingFields = [
    { id: 'sno', label: 'Sno', required: true },
    { id: 'name', label: 'Name', required: true },
    { id: 'mobileNumber', label: 'Mobile Number', required: true },
    { id: 'emailId', label: 'Email Id', required: true },
    { id: 'source', label: 'Source', required: true },
    { id: 'medium', label: 'Medium', required: true },
    { id: 'campaign', label: 'Campaign', required: true },
    { id: 'countryCode', label: 'Country Code', required: true },
  ];

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadedFields([]);
    }
  };

  const readExcelRows = async (file) => {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const firstSheet = workbook.SheetNames[0];
    if (!firstSheet) return [];
    const worksheet = workbook.Sheets[firstSheet];
    return XLSX.utils.sheet_to_json(worksheet, { defval: '', raw: false });
  };

  const validateCustomImportFields = (rows) => {
    if (!Array.isArray(rows) || !rows.length) return 'No Rows Found to Import';
    const firstRow = rows[0] || {};
    const headers = Object.keys(firstRow).map((key) => String(key || '').trim());
    if (!headers.includes('Sno')) {
      return "The Excel file does not contain the required 'Sno' column.";
    }
    return 'Import successful.';
  };

  const handleUploadTemplateFile = async () => {
    if (!selectedFile) {
      Swal.fire('Warning', 'Please select XLS or XLSX file.', 'warning');
      return;
    }

    const ext = String(selectedFile.name || '').split('.').pop()?.toLowerCase();
    if (!['xls', 'xlsx'].includes(ext)) {
      Swal.fire('Error', 'Provide only XLS or XLSX file', 'error');
      return;
    }

    try {
      setIsUploadingFile(true);
      const rows = await readExcelRows(selectedFile);

      if (!rows.length) {
        Swal.fire('Error', 'No Rows Found to Import', 'error');
        setUploadedFields([]);
        return;
      }

      if (rows.length > 10000) {
        Swal.fire('Error', 'Maximum 10000 Record allowed in one upload.', 'error');
        setUploadedFields([]);
        return;
      }

      const validationMessage = validateCustomImportFields(rows);
      if (validationMessage !== 'Import successful.') {
        Swal.fire('Error', validationMessage, 'error');
        setUploadedFields([]);
        return;
      }

      const columnNames = Object.keys(rows[0] || {}).map((column) => String(column || '').trim()).filter(Boolean);
      setUploadedFields(columnNames);
      Swal.fire('Success', validationMessage, 'success');
    } catch (error) {
      console.error('Template upload parse error:', error);
      Swal.fire('Error', 'Something wrong', 'error');
      setUploadedFields([]);
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleFieldMapping = (existingFieldId, mapping) => {
    setFieldMappings((prev) => ({
      ...prev,
      [existingFieldId]: mapping,
    }));
  };

  const getMappedValue = (field) => {
    const mapping = fieldMappings[field.id] || {};
    const mappingType = mapping.type || 'imported';
    const rawValue = mappingType === 'other'
      ? String(mapping.otherValue || '').trim()
      : String(mapping.value || '').trim();
    const isOtherField = mappingType === 'other' && rawValue !== '';
    const mappedValue = rawValue || 'Not Mapped';
    return {
      mappedValue,
      isOtherField,
      finalValue: isOtherField ? `${mappedValue} (Other Field)` : mappedValue,
    };
  };

  const buildReviewMappings = () => {
    return existingFields.map((field) => {
      const mapped = getMappedValue(field);
      return {
        key: field.id,
        label: field.label,
        ...mapped,
      };
    });
  };

  const onstart = () => {
    if (!templateName.trim()) {
      Swal.fire('Warning', 'Template name is required.', 'warning');
      return;
    }
    if (!uploadedFields.length) {
      Swal.fire('Warning', 'Please upload file first.', 'warning');
      return;
    }
    setCurrentStep(2);
  };

  const onnext = () => {
    const mappedRows = buildReviewMappings();
    setReviewMappings(mappedRows);
    setCurrentStep(3);
  };

  const navigateToStep = (step) => {
    if (step < 1 || step > 3) return;
    setCurrentStep(step);
  };

  const handlePrevious = () => {
    navigateToStep(currentStep - 1);
  };

  const onCancel = () => {
    if (typeof onClose === 'function') onClose();
  };

  const handleFinish = () => {
    const trimmedName = templateName.trim();
    if (!trimmedName) {
      Swal.fire('Warning', 'Template name is required and cannot be empty.', 'warning');
      return;
    }

    const mappedRows = reviewMappings.length ? reviewMappings : buildReviewMappings();
    const unmappedFields = mappedRows
      .filter((item) => item.mappedValue === 'Not Mapped')
      .map((item) => item.label);

    if (unmappedFields.length > 0) {
      Swal.fire('Warning', `Please map the following fields before saving:\n- ${unmappedFields.join('\n- ')}`, 'warning');
      return;
    }

    const mapFields = {};
    mappedRows.forEach((item) => {
      mapFields[item.label] = item.finalValue;
    });

    const templateData = {
      name: trimmedName,
      file: selectedFile,
      mappings: fieldMappings,
      mapFields,
      OriginalFields: JSON.stringify(uploadedFields),
    };
    onSubmit(templateData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Add Import Template</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Steps Header */}
        <div className="px-6 py-6 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-around max-w-4xl mx-auto">
            {/* Step 1 */}
            <div className="flex flex-col items-center gap-2">
              <div
                className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${
                  currentStep === 1
                    ? 'bg-blue-500 text-white'
                    : currentStep > 1
                    ? 'bg-gray-300 text-gray-700'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                1
              </div>
              <span
                className={`text-sm font-medium ${
                  currentStep === 1 ? 'text-blue-500' : 'text-gray-600'
                }`}
              >
                Template Name
              </span>
            </div>

            {/* Step 2 */}
            <div className="flex flex-col items-center gap-2">
              <div
                className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${
                  currentStep === 2
                    ? 'bg-blue-500 text-white'
                    : currentStep > 2
                    ? 'bg-gray-300 text-gray-700'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                2
              </div>
              <span
                className={`text-sm font-medium ${
                  currentStep === 2 ? 'text-blue-500' : 'text-gray-600'
                }`}
              >
                Map Fields
              </span>
            </div>

            {/* Step 3 */}
            <div className="flex flex-col items-center gap-2">
              <div
                className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${
                  currentStep === 3
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                3
              </div>
              <span
                className={`text-sm font-medium ${
                  currentStep === 3 ? 'text-blue-500' : 'text-gray-600'
                }`}
              >
                Review and complete
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-8 min-h-[400px]">
          {/* Step 1: Template Name */}
          {currentStep === 1 && (
            <div className="w-full">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Name
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Enter Template name"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select File with fields
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    accept=".xlsx,.xls"
                    className="flex-1 text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded file:border file:border-gray-300 file:text-sm file:font-medium file:bg-white file:text-gray-700 hover:file:bg-gray-50"
                  />
                  <button
                    type="button"
                    onClick={handleUploadTemplateFile}
                    disabled={isUploadingFile}
                    className="px-6 py-2 text-sm font-medium text-white bg-green-500 rounded hover:bg-green-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUploadingFile ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Map Fields */}
          {currentStep === 2 && (
            <div className="max-w-6xl">
              <div className="grid grid-cols-3 gap-4 mb-4 font-semibold text-sm text-gray-700">
                <div>Existing Field</div>
                <div>Uploaded Field</div>
                <div></div>
              </div>

              <div className="space-y-4 max-h-[500px] overflow-y-auto">
                {existingFields.map((field) => (
                  <div key={field.id} className="grid grid-cols-3 gap-4 items-center">
                    {/* Existing Field */}
                    <div>
                      <select className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500">
                        <option>{field.label}</option>
                      </select>
                    </div>

                    {/* Radio Buttons */}
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name={`mapping-${field.id}`}
                          checked={(fieldMappings[field.id]?.type || 'imported') === 'imported'}
                          onChange={() =>
                            handleFieldMapping(field.id, {
                              ...fieldMappings[field.id],
                              type: 'imported',
                              value: fieldMappings[field.id]?.value || '',
                            })
                          }
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-gray-700">From Imported File</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name={`mapping-${field.id}`}
                          checked={fieldMappings[field.id]?.type === 'other'}
                          onChange={() =>
                            handleFieldMapping(field.id, {
                              ...fieldMappings[field.id],
                              type: 'other',
                              otherValue: fieldMappings[field.id]?.otherValue || '',
                            })
                          }
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-gray-700">Other</span>
                      </label>
                    </div>

                    {/* Uploaded Field Dropdown */}
                    <div>
                      {(fieldMappings[field.id]?.type || 'imported') === 'other' ? (
                        <input
                          type="text"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-700"
                          value={fieldMappings[field.id]?.otherValue || ''}
                          onChange={(e) =>
                            handleFieldMapping(field.id, {
                              ...fieldMappings[field.id],
                              type: 'other',
                              otherValue: e.target.value,
                            })
                          }
                          placeholder="Enter other field value"
                        />
                      ) : (
                        <select
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-500"
                          value={fieldMappings[field.id]?.value || ''}
                          onChange={(e) =>
                            handleFieldMapping(field.id, {
                              ...fieldMappings[field.id],
                              type: 'imported',
                              value: e.target.value,
                            })
                          }
                        >
                          <option value="">
                            {uploadedFields.length > 0 ? 'select field name' : 'Nothing selected'}
                          </option>
                          {uploadedFields.map((uploadedField, idx) => (
                            <option key={idx} value={uploadedField}>
                              {uploadedField}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 text-right">
                <button className="text-blue-500 text-sm font-medium hover:underline">
                  Add more Fields
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Review and Complete */}
          {currentStep === 3 && (
            <div className="w-full">
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-700 mb-4 bg-gray-100 px-4 py-2 rounded">
                  Template Name
                </h3>
                <div className="px-4">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-gray-700">Name:</span>
                    <span className="text-gray-600">{templateName || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-4 bg-gray-100 px-4 py-2 rounded">
                  Map Fields
                </h3>
                <div className="px-4">
                  <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                    {(reviewMappings.length ? reviewMappings : buildReviewMappings()).map((field) => (
                      <div key={field.key} className="flex items-center justify-between">
                        <span className="font-semibold text-gray-700">{field.label}</span>
                        <span className="text-gray-600">{field.finalValue}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sw-toolbar-elm toolbar toolbar-bottom flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50" role="toolbar">
          <button
            type="button"
            onClick={handlePrevious}
            className={`btn sw-btn-prev sw-btn px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition ${currentStep > 1 ? '' : 'hidden'}`}
          >
            Previous
          </button>

          <button
            type="button"
            onClick={onstart}
            className={`btn sw-btn-proceed sw-btn px-6 py-2 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600 transition ${currentStep === 1 ? '' : 'hidden'}`}
          >
            Proceed
          </button>

          <button
            type="button"
            onClick={onnext}
            className={`btn sw-btn-next sw-btn px-6 py-2 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600 transition ${currentStep === 2 ? '' : 'hidden'}`}
          >
            Next
          </button>

          <button
            type="button"
            onClick={handleFinish}
            className={`btn btn-success finsih px-6 py-2 text-sm font-medium text-white bg-green-500 rounded hover:bg-green-600 transition ${currentStep === 3 ? '' : 'hidden'}`}
          >
            Finish
          </button>

          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddImportTemplate;
